import { DevTool } from "@hookform/devtools";
import { format, utcToZonedTime } from "date-fns-tz";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormState,
  useWatch
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";

import { fetcher, useFetchData, useFetchDataImmutable } from "../../apiHelpers";
import Button from "../../components/Button";
import ClientBasicInfoCard from "../../components/IntakeForm/ClientBasicInfoCard";
import FundingInformationCard from "../../components/IntakeForm/FundingInformationCard";
import IntakeFormDisclosure from "../../components/IntakeForm/IntakeFormDisclosure";
import IntakeFormToolbar from "../../components/IntakeForm/IntakeFormToolbar";
import SecurityExposureLimitCard from "../../components/IntakeForm/SecurityExposureLimitCard";
import SecurityRestrictionsCard from "../../components/IntakeForm/SecurityRestrictionsCard";
import SelectStrategyCard from "../../components/IntakeForm/SelectStrategyCard";
import SpecialInstructionsCard from "../../components/IntakeForm/SpecialInstructionsCard";
import TaxInformationCard from "../../components/IntakeForm/TaxInformationCard";
import Modal from "../../components/Modal";
import StatusBanner from "../../components/StatusBanner";
import { showToast } from "../../components/Toast";
import {
  API_ROUTES,
  CONTACT_US_EMAIL,
  ERROR_MESSAGE,
  REMOVED_STATES,
  RESTRICTION_OPTIONS,
  STRATEGY_LABEL,
} from "../../constants";
import {
  appendParamsToUrl,
  formatDate,
  getUrlSlug,
  isFundingSourceCashOnly,
  isFundingSourceSecuritiesOnly,
} from "../../helpers";
import useIntakeFormStore, {
  initialFormState,
} from "../../store/IntakeForm.store";
import useUserStore from "../../store/User.store";
import {
  getTaxLotDataInputFields,
  getUserSessionProposals,
  hasFilledAllRequired,
  processDataToFormValues,
} from "../../utils/helpers/intakeForm";

import {
  trackDraftOpen,
  trackDraftSave,
  trackProposalSubmit,
} from "../../utils/helpers/analytics";

import {
  getFormGroupFieldValues,
  getFormValue
} from './proposalHelpers';

//import { useHydration } from "../../hooks/useHydration";
import "./proposal.scss";

// eslint-disable-next-line sonarjs/cognitive-complexity
const Proposal = () => {
  // React Router hooks
  const { id: paramId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [firstSlug] = getUrlSlug(pathname);
  const [isForSaving, setIsFormSaving] = useState(false);

  const proposalId = paramId;
  const isDuplicating = paramId && firstSlug === "duplicate";
  const isSavingNew = !paramId && firstSlug === "proposals";
  const isAddingNewProposal = !paramId && pathname.includes("add");

  // Zustand Store hooks
  const {
    handleSetIntakeFormOptionsGeneric,
    handleSetIntakeFormOptionsSponsor,
    handleSetIntakeFormOptionsCustomBlend,
    handleSetIntakeFormValue,
    handleSetIntakeFormStates,
    handleSetIntakeFormSessionProposals,
    handleSetIntakeFormExclusionDataWithField,
  } = useIntakeFormStore.getState().actions;
  
  // SWR hooks
  const { data: genericInfoData } = useFetchDataImmutable({
    url: API_ROUTES.REFERENCE_DATA,
  });

  const { data: sponsorData } = useFetchDataImmutable({
    url: API_ROUTES.SPONSOR_DETAIL,
  });

  const { data: customIndexesData } = useFetchDataImmutable({
    url: API_ROUTES.GET_CUSTOM_INDEXES,
  });

  const proposalUrl = isNaN(proposalId)
    ? null
    : appendParamsToUrl(API_ROUTES.PROPOSAL_DETAIL, {
        transitionId: proposalId,
      });

  const {
    data: proposalDetailData,
    error: proposalDetailError,
    mutate,
  } = useFetchData({ url: proposalUrl });

  const [formData, formOptions, formState] = useIntakeFormStore(
    (state) => [
      state.data.formData,
      state.data.formOptions,
      state.data.formState,
    ],
    shallow
  );

  useEffect(() => {
    if (genericInfoData && (isDuplicating || isSavingNew || formData?.status === "Draft")) {
      handleSetIntakeFormOptionsGeneric(genericInfoData);
    }
  }, [
    genericInfoData,
    formData
  ]);

  useEffect(() => {
    if (sponsorData && (isDuplicating || isSavingNew || formData?.status === "Draft")) {
      handleSetIntakeFormOptionsSponsor(sponsorData);
    }
  }, [
    sponsorData,
    formData
  ]);

  useEffect(() => {
    if (customIndexesData && (isDuplicating || isSavingNew || formData?.status === "Draft")) {
      handleSetIntakeFormOptionsCustomBlend(customIndexesData);
    }
  }, [
    customIndexesData,
    formData
  ]);

  const user = useUserStore((state) => state.data.user);
  const token = user?.accessToken;

  // React hooks
  const [showSaveFailModal, setShowSaveFailModal] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [showFormFailModal, setShowFormFailModal] = useState(false);
  const [isCardsEnabled, setIsCardsEnabled] = useState(false);
  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [isDraftOpenTracked, setIsDraftOpenTracked] = useState(false);
  const [showTaxLotModal, setShowTaxLotModal] = useState(false);
  const [showFundingTypeModal, setShowFundingTypeModal] = useState(false);
  const [hasConfirmedFundingType, setHasConfirmedFundingType] = useState(false);
  const [newSelectedFundingTypeValue, setNewSelectedFundingTypeValue] =
    useState(null);
  const [submitBtn, setSubmitBtn] = useState(null);
  const [isSpecialInstructionsVisible, setIsSpecialInstructionsVisible] =
    useState(false);
  const [isSecurityRestrictionsVisible, setIsSecurityRestrictionsVisible] =
    useState(false);
  const [isSecurityExposureLimitsVisible, setIsSecurityExposureLimitsVisible] =
    useState(false);
  const [isFirstLoaded, setIsFirstLoaded] = useState(false);

  const id = useId();
  const transitionIdRef = useRef(0);

  // React Hook Form hooks
  
  const methods = useForm({
    mode: formState.isFormLocked ? "onSubmit" : "onTouched",
    defaultValues: processDataToFormValues(null, formOptions),
  });
  const watchedAccountName = useWatch({
    control: methods.control,
    name: "account",
  });
  const watchedRequiredToEnableFields = useWatch({
    control: methods.control,
    name: ["client", "account", "selectStrategy", "advisorName"],
  });
  const watchedRequiredFields = useWatch({
    control: methods.control,
    name: [
      "client",
      "account",
      "selectStrategy",
      "advisorName",
      "fundingSrc",
      "cashAmount",
    ],
  });

  const securitiesOnlyRequiredFields = useWatch({
    control: methods.control,
    name: ["client", "account", "selectStrategy", "advisorName", "fundingSrc"],
  });
  const { remove: removeTaxLotData, append: appendTaxLotData } = useFieldArray({
    control: methods.control,
    name: "taxLotData",
  });
  const { isDirty } = useFormState({
    control: methods.control,
  });


  // START getting strategy customizations to enable or disable
  // restirictions and security exposure buttons
  const selectedStrategy = formState.strategyDetail;
  const isLocked = formState.isFormLocked;
  const [strategyCustomizations, setStrategyCustomizations] = useState([]);
 
  useEffect(() => {
    if ((isDuplicating || formData?.status === "Draft" || isAddingNewProposal) && sponsorData && watchedRequiredFields) {      
      const sponsor = sponsorData.filter((item) => item.strategyCode === watchedRequiredFields[2]);
      const custs = sponsor[0]?.customizations;
      if (custs) {
        setStrategyCustomizations(
          custs
            .filter((item) => `${item.desc}`.toUpperCase() === "Y")
            .map(({ name }) => name)
        );
        handleSetIntakeFormStates({
          strategyCode: watchedRequiredFields[2]
        })
      }
    }
  },[sponsorData, formData, watchedRequiredFields])

  useEffect(() => {
      const customizations = selectedStrategy?.customizations
      ? selectedStrategy?.customizations.filter((item) => item.desc === `Y`)
      : selectedStrategy?.customizations;

      if (customizations) {
        setStrategyCustomizations(
          customizations
            .filter((item) => `${item.desc}`.toUpperCase() === "Y")
            .map(({ name }) => name)
        );
      }
  }, [selectedStrategy])
  // END getting strategy customizations to enable or disable
  // restirictions and security exposure buttons

  let buttonText = "Enter";
  if (
    formState.isFormLocked ||
    (proposalId && proposalDetailData && genericInfoData && sponsorData)
  ) {
    buttonText = "View";
  } else {
    buttonText = "Enter";
  }

  useEffect(() => {
    if (formData?.status === "Draft" && !isDraftOpenTracked) {
      trackDraftOpen(
        formState.strategyDetail?.sector,
        formState.strategyDetail?.strategyName
      );
      setIsDraftOpenTracked(true);
    }
  }, [formData, formState, isDraftOpenTracked]);

  useEffect(() => {
    if (paramId) return;

    methods.setValue("advisorSponsor", user?.sponsor);
  }, [user, paramId, methods]);

  const { errors } = methods.formState;
  const currentErrors = Object.keys(errors);

  const minCredQual = formState.strategyDetail?.minCredQual;

  const processExclusions = (
    sector,
    stateSelections,
    sectorSelections,
    statePreference
  ) => {
    if (sector === STRATEGY_LABEL.corp) {
      const sectors = [];
      sectorSelections.map((val) => {
        return sectors.push({
          exclusionType: "Sector",
          exclusion: val.sector,
          weight: val.weight.toString(),
        });
      });
      return sectors;
    } else if (sector === STRATEGY_LABEL.muni) {
      const states = [];
      !statePreference &&
        stateSelections.map((val) => {
          return states.push({
            exclusionType: "State",
            exclusion: val.state,
            weight: val.weight,
          });
        });
      return states;
    }
    return [];
  };

  const processMaturityPreference = useCallback(
    (userMaturityPreference) => {
      const { maturityLowerBound, maturityUpperBound } =
        formState.strategyDetail;
      const [defaultLowerBound, defaultUpperBound] = [
        maturityLowerBound,
        maturityUpperBound,
      ];

      let [lowerBound, upperBound] = userMaturityPreference || [
        defaultLowerBound,
        defaultUpperBound,
      ];

      // Ensure upper bound is even when set to 20
      if (defaultUpperBound === "20" && upperBound % 2 === 1) {
        upperBound += 1;
      }

      // Clamp upper bound to the maximum allowed value
      upperBound = Math.min(upperBound, defaultUpperBound);

      // Ensure lower bound is within bounds
      lowerBound = Math.min(lowerBound, defaultUpperBound);

      return [lowerBound, upperBound];
    },
    [formState.strategyDetail]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const processFormValues = useCallback(() => {
    const values = methods.getValues();
    if (isNaN(proposalId)) transitionIdRef.current = 0;
    const stratOptions = formOptions?.strategies[values.selectCategory];
    const stratName = stratOptions.find((opt) => opt.value === values.selectStrategy).label;
    const formValues = {
      transitionId: transitionIdRef.current,
      strategyName: formState.strategyDetail?.strategyName ? formState.strategyDetail?.strategyName : stratName,
      client: values.client,
      account: values.account,
      advisorName: values.advisorName,
      advisorEmail: getFormValue(values.advisorEmail),
      sendReportToAdvisor: getFormValue(values.sendReportToAdvisor),
      emailCC: getFormValue(values.emailCC),
      sendReporttoCC: getFormValue(values.sendReporttoCC),
      fundingSrc: values.fundingSrc,
      territory: getFormValue(values.territory),
      salesforceId: getFormValue(values.salesforceId),
      advisorSponsor: getFormValue(values.advisorSponsor),
      totalMarketValue: getFormValue(values.totalMarketValue),
      groupName: getFormValue(values.companyName),
      purchaseAmtBonds: getFormValue(values.purchaseAmtBonds, false),
      taxState: getFormValue(values.taxState),
      maxRealizedGainYtd: getFormValue(values.maxRealizedGainShort, 0),
      maxRealizedLTGain: getFormValue(values.maxRealizedGainLong, 0),
      taxBudgetPerYear: getFormValue(values.taxBudgetPerYear, 0),
      stTaxRate: getFormValue(parseFloat(values.stTaxRate) / 100, 0)?.toFixed(2),
      ltTaxRate: getFormValue(parseFloat(values.ltTaxRate) / 100, 0)?.toFixed(2),
      yearsToTransition: getFormValue(values.yearsToTransition),
      carryFwdLosses: getFormValue(values.carryFwdLosses, 0),
      cashToRaise: getFormValue(values.cashToRaise, 0),

      stYtdRealizedG: getFormValue(values.stYtdRealizedG, 0),
      ltYtdRealizedG: getFormValue(values.ltYtdRealizedG, 0),
      ltYtdRealizedL: getFormValue(values.ltYtdRealizedL, 0),
      stYtdRealizedL: getFormValue(values.stYtdRealizedL, 0),
      creditQuality: getFormValue(values.creditQuality),
      yieldSpecialInstructions: getFormValue(values.yieldPreference),
      sectorSpecialInstructions: getFormValue(values.sectorPreference),
      esgSpecialInstructions: getFormValue(values.esgPreference),
      cashAmount: getFormValue(values.cashAmount),
      templateTaxLot: getFormGroupFieldValues(values.taxLotData),
      templateSecRestriction: getFormGroupFieldValues(values.securityLimitData),
      esgScreen: getFormValue(values.esgScreenPreference),
    };
    if (isFundingSourceSecuritiesOnly(values.fundingSrc)) {
      formValues.cashAmount = null;
    }

    if (isFundingSourceCashOnly(values.fundingSrc)) {
      formValues.templateTaxLot = [];
    } else {
      // Tax Lot Group Field
      const taxLotData = getFormGroupFieldValues(values.taxLotData);
      if (taxLotData?.length > 0) {
        formValues.templateTaxLot = taxLotData.map((lot) => {
          return {
            ...lot,
            purchaseDate: formatDate(lot.purchaseDate),
          };
        });
      }
    }

    if (
      formState.strategyDetail?.strategyName === "SMArt Custom Blend" ||
      formState.strategyDetail?.strategyName ===
        "SMArt Custom Blend (70% S&P500 / 30% MSCI EAFE ADR)"
    ) {
      setIsFormSaving(true);
      let tempCustBlendStr = JSON.stringify(formState.customBlend);
      const regex = /"(-|)([0-9]+(?:\.[0-9]+)?)"/g 
      tempCustBlendStr = tempCustBlendStr.replace(regex, '$1$2')
      Object.assign(formValues, {
        customBlend: tempCustBlendStr,
      });
    } else {
      Object.assign(formValues, { customBlend: null });
    }

    //Special Instructions
    if (formState.strategyDetail.assetClass !== "Equity") {
      const maturityPreferenceValue = processMaturityPreference(
        values.maturityPreference
      );

      Object.assign(formValues, {
        credit_quality_SI: getFormValue(values.creditQuality),
        statePreferenceOption: getFormValue(values.statePreference),
        maturitySpecialInstructions: getFormValue(
          maturityPreferenceValue
        )?.join("-"),
        templateExclusions: processExclusions(
          values.selectCategory,
          values.stateExclusions,
          values.sectorExclusions,
          values.statePreference
        ),
      });
    } else {
      Object.assign(formValues, {
        templateExclusions: [
          ...values.gics_industry_group.map((val) => {
            return {
              exclusionType: "gics_industry_group",
              exclusion: val.gics_industry_group,
              weight: val.weight.toString(),
            };
          }),
          ...values.esg_screens.map((val) => {
            return {
              exclusionType: "esg_screens",
              exclusion: val.name,
              weight: null,
            };
          }),
        ],
      });
    }

    // Security Restriction Group Field
    const accountRestrictionData = getFormGroupFieldValues(
      values.accountRestrictionData
    );
    if (accountRestrictionData?.length > 0) {
      formValues.templateSecRestriction = [
        ...formValues.templateSecRestriction,
        ...accountRestrictionData.map((lot) => ({
          ...lot,
          restrictBuy: lot?.restriction === RESTRICTION_OPTIONS.buy,
          restrictSell: lot?.restriction === RESTRICTION_OPTIONS.sell,
          restrictTrade: lot?.restriction === RESTRICTION_OPTIONS.trade,
          restrictHold: lot?.restriction === RESTRICTION_OPTIONS.hold,
        })),
      ];
    }

    return formValues;
  }, [formState, methods, proposalId, minCredQual, processMaturityPreference]);

  const checkRequiredFields = useCallback(
    (requiredFields) => hasFilledAllRequired(requiredFields),
    []
  );

  const handlePostProposal = async (data) => {
    return fetcher({
      url: API_ROUTES.SAVE_PROPOSAL,
      token,
      method: "POST",
      data,
    });
  };

  const setUserSessionProposals = (data, transitionId) => {
    const currentSessionProposals = formOptions?.sessionProposals || [];
    const { account: name, client, totalMarketValue, status } = data;
    const recentProposalData = {
      transitionId,
      name,
      client,
      totalMarketValue,
      status: status.toLowerCase(),
    };

    const sessionProposals = currentSessionProposals?.map((proposal) =>
      proposal.transitionId === transitionId ? recentProposalData : proposal
    );

    if (
      !sessionProposals.find(
        (proposal) => proposal.transitionId === transitionId
      )
    ) {
      sessionProposals.push(recentProposalData);
    }

    handleSetIntakeFormSessionProposals(sessionProposals);

    // Add transitionId to user session proposals
    sessionStorage.setItem(
      "user-session-proposals",
      JSON.stringify(sessionProposals)
    );
  };

  const handleShowToast = useCallback(
    (accountName = watchedAccountName, hasDuplicated, toastId) => {
      showToast(
        () => (
          <span>
            <strong>{accountName}</strong> has been successfully{" "}
            {hasDuplicated ? "duplicated" : "saved"}.
          </span>
        ),
        toastId
      );
    },
    [watchedAccountName]
  );

  const getExclusionData = useCallback(
    async (url, field) => {
      const data = await fetcher({
        url,
        token,
      });
      const filteredData = data.filter(
        (el) => !REMOVED_STATES.includes(el[field])
      );
      handleSetIntakeFormExclusionDataWithField(filteredData, field);
    },
    [token, handleSetIntakeFormExclusionDataWithField]
  );

  const checkTaxLotDataPurchaseDates = (templateTaxLot) => {
    return templateTaxLot.some(
      ({ purchaseDate }) =>
        purchaseDate === "" || purchaseDate === "NaN-NaN-NaN"
    );
  };

  const hasPurchasedDateEmpty = (templateTaxLot) => {
    return (
      templateTaxLot.length > 0 &&
      checkTaxLotDataPurchaseDates(templateTaxLot || [])
    );
  };

  const checkTaxLotDataCostBasis = (templateTaxLot) => {
    return templateTaxLot.some(({ costBasis }) => costBasis === "");
  };

  const hasPurchasedCostBasisEmpty = (templateTaxLot) => {
    return (
      templateTaxLot.length > 0 &&
      checkTaxLotDataCostBasis(templateTaxLot || [])
    );
  };

  const updateTaxLotDataEmptyDefaultFields = (templateTaxLot, assetClass) => {
    if (
      !hasPurchasedDateEmpty(templateTaxLot) &&
      !hasPurchasedCostBasisEmpty(templateTaxLot)
    )
      return false;

    templateTaxLot.map((item) => {
      if (
        !item?.purchaseDate ||
        item.purchaseDate === "" ||
        item.purchaseDate === "NaN-NaN-NaN"
      ) {
        const date = new Date();
        const formattedDate = format(utcToZonedTime(date), "yyyy-MM-dd");
        item.purchaseDate = formattedDate;
      }

      if (!item?.costBasis || item?.costBasis === "") {
        item.costBasis = assetClass === "Equity" ? 0 : item?.qty || 0;
      }

      return item;
    });
  };

  const handleFormError = (errors, e) => console.log(errors, e);

  const handleFormSubmit = async () => {
    if (submitBtn) {
      submitBtn.disabled = true;
    }
    const data = processFormValues();

    if (
      hasPurchasedDateEmpty(data.templateTaxLot) ||
      hasPurchasedCostBasisEmpty(data.templateTaxLot)
    ) {
      setShowTaxLotModal(true);
    } else {
      proceedFormSubmit(data);
    }
  };

  const proceedFormSubmit = async (data) => {
    data.status = "Submit";

    trackProposalSubmit(formState.strategyDetail.strategyName);

    // set empty fields with default values
    updateTaxLotDataEmptyDefaultFields(
      data.templateTaxLot,
      formState.strategyDetail.assetClass
    );

    const response = await handlePostProposal(data);
    if (response.returnCode === "200") {
      transitionIdRef.current = response.transitionId;
      setUserSessionProposals(data, response.transitionId);
      setShowSubmitSuccessModal(true);
      setIsFormSaving(false);
    } else {
      setIsFormSaving(false);
      if (submitBtn) {
        submitBtn.disabled = false;
      }
      setShowFormFailModal(true);
    }
  };

  const handleFormSave = async (e) => {
    const saveBtn = e.target;
    const data = processFormValues();
    setIsFormSaving(false);
    proceedFormSave(data, saveBtn);
  };

  const proceedFormSave = async (data, saveBtn) => {
    saveBtn.disabled = true;
    data.status = "Draft";

    trackDraftSave(
      formState.strategyDetail.sector,
      formState.strategyDetail.strategyName
    );

    let proposalRequiredFields = watchedRequiredFields;
    if (isFundingSourceSecuritiesOnly(data.fundingSrc)) {
      proposalRequiredFields = securitiesOnlyRequiredFields;
    }

    if (
      !checkRequiredFields(proposalRequiredFields) ||
      (isFundingSourceCashOnly(data.fundingSrc) && !data.cashAmount)
    ) {
      setShowSaveFailModal(true);
      setIsFormSaving(false);
      saveBtn.disabled = false;
      return false;
    }

    // set empty fields with default values
    updateTaxLotDataEmptyDefaultFields(
      data.templateTaxLot,
      formState.strategyDetail.assetClass
    );

    const response = await handlePostProposal(data);
    setIsFormSaving(false);
    if (response.returnCode === "200") {
      saveBtn.disabled = false;
      transitionIdRef.current = response.transitionId;

      //  Initiate Toast on Save of Existing Draft Proposal
      handleShowToast(watchedAccountName, false, "successSaveToast");

      setUserSessionProposals(data, response.transitionId);

      if (data.customBlend) {
        handleSetIntakeFormStates({
          customBlend: JSON.parse(data.customBlend),
        });
      }
      //handleSetIntakeFormValue(data);
      mutate(data);

      let navState = null;

      if (isDuplicating) {
        navState = {
          state: {
            accountName: watchedAccountName,
            successDuplicate: true,
          },
        };
      }
      if (isSavingNew) {
        navState = {
          state: {
            accountName: watchedAccountName,
            successSaveNew: true,
          },
        };
      }

      navigate(`/proposals/${response.transitionId}`, {
        replace: true,
        ...navState,
      });
      //hack for getting react hook form to not lose all values
      window.location.reload(false);
    } else {
      setShowFormFailModal(true);
      saveBtn.disabled = false;
    }
  };

  const handleOnSubmit = (e) => {
    const submitButton = e.target.querySelector('button[type="submit"]');
    submitButton.disabled = true;
    setSubmitBtn(submitButton);
    e.preventDefault();
    if (currentErrors.length === 0) {
      methods.handleSubmit(handleFormSubmit, handleFormError)(e);
    }
  };

  const resetSpecialInstructionField = useCallback(
    (strategy) => {
      const defaultPositiveValue = "INCLUDE=Y|MIN=0|MAX=100";
      const defaultNegativeValue =
        strategy === STRATEGY_LABEL.muni
          ? "INCLUDE=N|MIN=0|MAX=0"
          : "INCLUDE=Y|MIN=0|MAX=100";

      const resetPreferences = (field) => methods.setValue(field, undefined);

      resetPreferences("maturityPreference");
      resetPreferences("statePreference");
      resetPreferences("esgPreference");

      methods.setValue("sectorExclusions", []);
      methods.setValue("stateExclusions", []);
      methods.setValue("gics_industry_group", []);
      methods.setValue("name", []);
      methods.setValue(
        "statePreference",
        proposalDetailData?.statePreferenceOption
      );
      methods.setValue(
        "esgPreference",
        proposalDetailData?.esgSpecialInstructions
      );
      methods.setValue("customBlend", proposalDetailData?.customBlend);
      if (proposalId && proposalDetailData) {
        handleSetIntakeFormValue({
          ...proposalDetailData,
          templateExclusions: [],
          statePreference: proposalDetailData?.statePreferenceOption,
          esgPreference: proposalDetailData?.esgSpecialInstructions,
        });
      }
    },
    [methods, isDirty, proposalId, proposalDetailData]
  );

  useEffect(() => {
    if (!formState?.strategyDetail?.strategyCode) return;

    if (isFirstLoaded) setIsFirstLoaded(false);
    //else resetSpecialInstructionField(formState?.category);

    const { strategyCode, sector } = formState.strategyDetail;
    let exclusionURLs = [];
    let fields = [];

    if (sector === STRATEGY_LABEL.corp) {
      exclusionURLs = [API_ROUTES.SECTOR_EXCLUSIONS];
      fields = ["sector"];
    } else if (sector === STRATEGY_LABEL.muni) {
      exclusionURLs = [API_ROUTES.STATE_EXCLUSIONS];
      fields = ["state"];
    } else if (sector === "Direct Indexing") {
      exclusionURLs = [
        API_ROUTES.INDUSTRY_EXCLUSIONS,
        API_ROUTES.ESG_SCREENS_EXCLUSIONS,
      ];
      fields = ["gics_industry_group", "name"];
    } else return;

    for (let i = 0; i < exclusionURLs.length; i++) {
      const url = appendParamsToUrl(exclusionURLs[i], { strategyCode });
      getExclusionData(url, fields[i]);
    }
  }, [
    isFirstLoaded,
    formState.strategyDetail,
    getExclusionData,
    resetSpecialInstructionField,
  ]);

  useEffect(() => {
    // If viewing a proposal detail
    // Else, adding a new proposal
    if (proposalId && proposalDetailData && genericInfoData && sponsorData) {
      handleSetIntakeFormValue(proposalDetailData);
      const formValues = processDataToFormValues(proposalDetailData, {
        strategyDetails: sponsorData,
      });

      // If duplicating or viewing a proposal detail
      if (isDuplicating) {
        const newAccountName = `Copy of ${proposalDetailData.account}`;
        formValues.account = newAccountName.substring(0, 44);
        formValues.status = "draft";
      } else {
        transitionIdRef.current = proposalDetailData.transitionId;
        handleSetIntakeFormStates({
          status: proposalDetailData.status.toLowerCase(), // eslint-disable-line
          isFormLocked:
            proposalDetailData.status.toLowerCase() !== "draft" && // eslint-disable-line
            proposalDetailData.status.toLowerCase() !== "needs attention", // eslint-disable-line
        });
      }

      setIsFirstLoaded(true);

      // Reset Hook Form field values
      methods.reset(formValues);
    }

    return () => {
      if (proposalDetailData) {
        handleSetIntakeFormValue(proposalDetailData);
        handleSetIntakeFormStates(initialFormState);
      }
    };
    // eslint-disable-next-line
  }, [
    proposalDetailData,
    handleSetIntakeFormValue,
    handleSetIntakeFormStates,
    methods,
    sponsorData,
    proposalId,
    paramId,
    isDuplicating,
    navigate,
  ]);

  // TODO: This redirection should work
  // once the correct error status is sent from server when fetch fail
  useEffect(() => {
    if (proposalDetailError?.status) {
      navigate(`/404`);
    }
  }, [proposalDetailError, navigate]);

  useEffect(() => { 
    setIsCardsEnabled(checkRequiredFields(watchedRequiredToEnableFields));
    setButtonsEnabled(checkRequiredFields(watchedRequiredToEnableFields));
    (isDuplicating || isSavingNew || formData?.status.toLowerCase() === "draft"  // eslint-disable-line
      || formData?.status.toLowerCase() === "needs attention") ? setButtonsEnabled(true) : setButtonsEnabled(false); // eslint-disable-line
  }, [checkRequiredFields, watchedRequiredToEnableFields]);

  useEffect(() => {
    handleSetIntakeFormStates({
      isFormDirty: isDirty,
    });
  }, [isDirty, handleSetIntakeFormStates]);

  useEffect(() => {
    const userSessionProposals = getUserSessionProposals();
    handleSetIntakeFormSessionProposals(userSessionProposals);
  }, [handleSetIntakeFormSessionProposals]);

  const [enableExpLimits, setEnableExpLimits] = useState(false);
  const [enableSecRestr, setEnableSecRestr] = useState(false);

/* eslint-disable */
  useEffect(() => {
    if(watchedRequiredFields[2] === undefined){
      setEnableSecRestr(true);
      setEnableExpLimits(true);
    } else {
      setEnableSecRestr(false);
      setEnableExpLimits(false);
    }

    if(watchedRequiredFields[2] && strategyCustomizations?.includes("custSecurityRestr")){
      setEnableSecRestr(false);
    } else {
      setEnableSecRestr(true);
    }

    if(watchedRequiredFields[2] && strategyCustomizations?.includes("custSecExpLimits")){
      setEnableExpLimits(false);
    } else {
      setEnableExpLimits(true);
    }
  }, [watchedRequiredFields[2], strategyCustomizations])

  if (proposalId && !proposalDetailData) {
    return false;
  }

  return (
    <>
      <FormProvider {...methods}>
        <div className="proposal-page">
          {formState?.status &&
            formState?.status.toLowerCase() !== "draft" &&
            formState?.status.toLowerCase() !== "needs attention" &&
            !isDuplicating && (
              <StatusBanner
                status={formState?.status?.toLowerCase()}
                id={proposalId}
                clientQuestionnaireId={proposalDetailData?.questionnaireId}
              />
            )}

          <header className="section-title">
            <h1>
              {watchedAccountName
                ? watchedAccountName?.replace(/[^A-Za-z0-9& ]/g, "")
                : formData?.account || "New Proposal"}
            </h1>
          </header>

          <form className="intake-form" onSubmit={handleOnSubmit}>
            <div className="section-content">
              <ClientBasicInfoCard
                id={`${id}-intakeFormCard-1`}
                headerIcon={"icon-globe"}
              />

              <SelectStrategyCard
                id={`${id}-intakeFormCard-2`}
                headerIcon={"icon-gold"}
              />

              <FundingInformationCard
                id={`${id}-intakeFormCard-3`}
                headerIcon={"icon-money-bag"}
                isEnabled={isCardsEnabled}
                setShowFundingTypeModal={setShowFundingTypeModal}
                setHasConfirmedFundingType={setHasConfirmedFundingType}
                setNewSelectedValue={setNewSelectedFundingTypeValue}
                hasConfirmedFundingType={hasConfirmedFundingType}
              />

              <TaxInformationCard
                id={`${id}-intakeFormCard-4`}
                headerIcon={"icon-money-drop"}
                isEnabled={isCardsEnabled}
              />
              <div className="button-wrapper">
                <Button
                  size="small"
                  type="button"
                  disabled={watchedRequiredFields[2] === undefined}
                  onClick={() =>
                    setIsSpecialInstructionsVisible(
                      !isSpecialInstructionsVisible
                    )
                  }
                >
                  {isSpecialInstructionsVisible ? "Hide" : buttonText}
                  {" Special Instructions"}
                </Button>
                {strategyCustomizations && (
                  <>
                    <Button
                      size="small"
                      type="button"
                      disabled={enableSecRestr}
                      onClick={() =>
                        setIsSecurityRestrictionsVisible(
                          !isSecurityRestrictionsVisible
                        )
                      }
                    >
                      {isSecurityRestrictionsVisible ? "Hide" : buttonText}
                      {" Security Restrictions"}
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      disabled={enableExpLimits}
                      onClick={() =>
                        setIsSecurityExposureLimitsVisible(
                          !isSecurityExposureLimitsVisible
                        )
                      }
                    >
                      {isSecurityExposureLimitsVisible ? "Hide" : buttonText}
                      {" Security Exposure Limits"}
                    </Button>
                  </>
                )}
                
              </div>

              {isSpecialInstructionsVisible && (
                <SpecialInstructionsCard
                  id={`${id}-intakeFormCard-3`}
                  headerIcon={"icon-drop"}
                  isVisible={isSpecialInstructionsVisible}
                  isEnabled={isCardsEnabled}
                />
              )}
              {isSecurityRestrictionsVisible && (
                <SecurityRestrictionsCard
                  id={`${id}-intakeFormCard-5`}
                  headerIcon={"icon-globe"}
                  isEnabled={isCardsEnabled}
                  setShowFundingTypeModal={setShowFundingTypeModal}
                  setHasConfirmedFundingType={setHasConfirmedFundingType}
                  setNewSelectedValue={setNewSelectedFundingTypeValue}
                  hasConfirmedFundingType={hasConfirmedFundingType}
                />
              )}

              {isSecurityExposureLimitsVisible && (
                <SecurityExposureLimitCard
                  id={`${id}-intakeFormCard-6`}
                  headerIcon={"icon-drop"}
                  isEnabled={isCardsEnabled}
                />
              )}

              <IntakeFormDisclosure />
            </div>

            <IntakeFormToolbar
              onSave={handleFormSave}
              isDuplicating={isDuplicating}
            />
          </form>
        </div>
      </FormProvider>

      <Modal
        variation="alert"
        show={showSaveFailModal}
        onClose={() => setShowSaveFailModal()}
        heading="Save unsuccessful!"
        subtitle="You must complete all required fields before you can save this proposal."
        cancelButton={{
          variant: "outline",
          children: "Close",
        }}
        confirmButton={null}
        iconName="system-warning-filled"
        iconTheme="error"
      />

      <Modal
        variation="alert"
        show={showSubmitSuccessModal}
        onClose={() => navigate(`/proposals`, { replace: true })}
        onConfirm={() => {
          window.location.href = `mailto:${CONTACT_US_EMAIL}`;
        }}
        heading="Submission successful!"
        subtitle={`An email confirmation was sent to ${user?.email} with the estimated report
            availability time. If you have questions or would like to schedule an
            appointment, please contact us.`}
        cancelButton={{
          variant: "outline",
          children: "Close",
        }}
        confirmButton={{
          variant: "primary",
          children: "Contact Us",
        }}
        iconName="system-checkmark-filled"
        iconTheme="success"
      />

      <Modal
        variation="alert"
        show={showFormFailModal}
        onClose={() => setShowFormFailModal()}
        heading="Form Error!"
        subtitle={ERROR_MESSAGE.SUBTITLE.TRY_AGAIN}
        cancelButton={{
          variant: "outline",
          children: "Close",
        }}
        confirmButton={null}
        iconName="system-warning-filled"
        iconTheme="error"
      />

      <Modal
        variation="alert"
        show={showTaxLotModal}
        onClose={() => setShowTaxLotModal(false)}
        onConfirm={() => {
          const data = processFormValues();

          setShowTaxLotModal(false);
          proceedFormSubmit(data);
        }}
        heading="Tax Lot Data important details"
        subtitle="In step 5, if any fields for the Purchase date are left empty, they will default to the current submission date. For Cost basis, empty fields default to 0 for equity strategies and quantity/par for fixed income strategies."
        suggestions="Please update any empty fields or select proceed and submit to use the defaults."
        cancelButton={{
          variant: "outline",
          children: "Edit empty fields",
        }}
        confirmButton={{
          variant: "primary",
          children: "Proceed and submit",
        }}
      />

      <Modal
        variation="alert"
        show={showFundingTypeModal}
        onClose={() => {
          setShowFundingTypeModal(false);
          setHasConfirmedFundingType(false);
        }}
        onConfirm={() => {
          setShowFundingTypeModal(false);
          setHasConfirmedFundingType(true);

          if (
            isFundingSourceCashOnly(newSelectedFundingTypeValue) &&
            removeTaxLotData
          ) {
            setTimeout(() => {
              removeTaxLotData();
              appendTaxLotData(
                getTaxLotDataInputFields(formState).reduce(
                  (acc, { name }) => ({
                    ...acc,
                    [name]: "",
                  }),
                  {}
                ),
                true
              );
            }, 400);
          }
        }}
        heading="Funding information will not be saved"
        subtitle="If you change your funding type, any data you added in funding information will be lost."
        cancelButton={{
          variant: "outline",
          children: "Cancel",
        }}
        confirmButton={{
          variant: "primary",
          children: "Change Funding Type",
        }}
      />

      {process.env.REACT_APP_ENV === "local" && (
        <DevTool control={methods.control} />
      )}
    </>
  );
};

export default Proposal;
